import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import type { DragEndEvent } from '@dnd-kit/core'
import { FloorPlanCanvas } from '@/components/floor-plan/FloorPlanCanvas'
import { FloorPlanToolbar } from '@/components/floor-plan/FloorPlanToolbar'
import { TableDetailPanel } from '@/components/floor-plan/TableDetailPanel'
import { AreaDetailPanel } from '@/components/floor-plan/AreaDetailPanel'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import { useAreas, useCreateArea } from '@/lib/queries/useAreas'
import {
  useTables,
  useCreateTable,
  useBatchUpdateTablePositions,
  useTableCombinations,
  useCreateTableCombination,
  useUpdateTableCombination,
  useDeleteTableCombination,
} from '@/lib/queries/useTables'
import { useBookings } from '@/lib/queries/useBookings'
import { usePOSOrders } from '@/lib/queries/usePOS'
import { useUIStore } from '@/lib/store/useUIStore'
import { useNavigate } from 'react-router-dom'
import { format, addDays, parseISO, addMinutes, isBefore } from 'date-fns'
import { getDateLocale } from '@/lib/utils/date'
import {
  useFloorPlanLayouts,
  useCreateFloorPlanLayout,
  useUpdateFloorPlanLayout,
  useDeleteFloorPlanLayout,
} from '@/lib/queries/useFloorPlanLayouts'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { useAppStore } from '@/lib/store/useAppStore'
import type { TableMetadata, TableShape } from '@/lib/types/table'
import { AREA_HEIGHT, AREA_HEADER, AREA_WIDTH } from '@/components/floor-plan/AreaLayer'
import { getShape } from '@/components/floor-plan/TableShapeRenderer'
import { ShapePicker } from '@/components/floor-plan/ShapePicker'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { isOrderBooking } from '@/lib/utils/bookingSemantics'

function findFreePosition(
  areaTables: Array<{ id: number; seatsMax: number; shape?: TableShape }>,
  currentPositions: Record<string, { x: number; y: number }>,
  newDims: { width: number; height: number },
  areaW: number,
  areaH: number,
  gs: number,
): { x: number; y: number } {
  for (let y = gs; y + newDims.height + gs <= areaH; y += gs) {
    for (let x = gs; x + newDims.width + gs <= areaW; x += gs) {
      const blocked = areaTables.some((t) => {
        const tPos = currentPositions[String(t.id)]
        if (!tPos) return false
        const { width: tw, height: th } = getShape(t.seatsMax, t.shape)
        return (
          x < tPos.x + tw &&
          x + newDims.width > tPos.x &&
          y < tPos.y + th &&
          y + newDims.height > tPos.y
        )
      })
      if (!blocked) return { x, y }
    }
  }
  return { x: gs, y: gs }
}

function snapAdjacentTo(
  dragPos: { x: number; y: number },
  dw: number,
  dh: number,
  targetPos: { x: number; y: number },
  tw: number,
  th: number,
): { x: number; y: number } {
  const centerDx = (dragPos.x + dw / 2) - (targetPos.x + tw / 2)
  const centerDy = (dragPos.y + dh / 2) - (targetPos.y + th / 2)
  if (Math.abs(centerDx) >= Math.abs(centerDy)) {
    return centerDx >= 0
      ? { x: targetPos.x + tw, y: dragPos.y }
      : { x: targetPos.x - dw, y: dragPos.y }
  }
  return centerDy >= 0
    ? { x: dragPos.x, y: targetPos.y + th }
    : { x: dragPos.x, y: targetPos.y - dh }
}

export default function FloorPlanPage() {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { data: areasResp, isLoading: loadingAreas } = useAreas()
  const { data: tablesResp } = useTables()
  const { data: combinationsResp } = useTableCombinations()
  const { data: layoutsResp } = useFloorPlanLayouts()
  const batchSave = useBatchUpdateTablePositions()
  const createTable = useCreateTable()
  const createArea = useCreateArea()
  const createCombination = useCreateTableCombination()
  const updateCombination = useUpdateTableCombination()
  const deleteCombination = useDeleteTableCombination()
  const createLayout = useCreateFloorPlanLayout()
  const updateLayout = useUpdateFloorPlanLayout()
  const deleteLayout = useDeleteFloorPlanLayout()
  const { restaurantId } = useAppStore()

  const {
    positions,
    selectedTableId,
    selectedAreaId,
    selectTable,
    selectArea,
    setPosition,
    initPositions,
    dirtyIds,
    clearDirty,
    zoom,
    snapToGrid,
    gridSize,
    timelineDate,
    setTimelineDate,
    timelineHour,
    mode,
    setMode,
    activeLayoutId,
    setActiveLayoutId,
    isEditingDateLayout,
    setIsEditingDateLayout,
    setRejectedTableIds,
    markDirty,
  } = useFloorPlanStore()

  const { data: bookingsResp } = useBookings({ date: timelineDate })
  const { data: upcomingResp } = useBookings({
    dateFrom: format(new Date(), 'yyyy-MM-dd'),
    dateTo: format(addDays(new Date(), 60), 'yyyy-MM-dd'),
    pageSize: 200,
  })

  // POS mode
  const navigate = useNavigate()
  const setPosSelectedTableDocumentId = useUIStore((s) => s.setPosSelectedTableDocumentId)
  const setPosTab = useUIStore((s) => s.setPosTab)
  const todayStr = format(new Date(), 'yyyy-MM-dd')
  const { data: posOrdersResp } = usePOSOrders(mode === 'pos' ? todayStr : undefined)
  const posOrders = (posOrdersResp as any)?.data ?? []

  const posOrdersByTableDocumentId = useMemo(() => {
    if (mode !== 'pos') return undefined
    const map = new Map<string, 'none' | 'open' | 'in_progress' | 'paid'>()
    for (const order of posOrders) {
      if (!order.table?.documentId || order.status === 'voided') continue
      const existing = map.get(order.table.documentId)
      const statusKey = order.status === 'open' ? 'open'
        : order.status === 'sent' || order.status === 'partially_paid' ? 'in_progress'
        : order.status === 'paid' ? 'paid'
        : 'none'
      if (!existing || statusKey !== 'none') map.set(order.table.documentId, statusKey)
    }
    return map
  }, [mode, posOrders])

  const handlePOSTableClick = useCallback((tableDocumentId: string) => {
    setPosTab('tables')
    setPosSelectedTableDocumentId(tableDocumentId)
    navigate('/pos')
  }, [navigate, setPosTab, setPosSelectedTableDocumentId])

  const areas = areasResp?.data ?? []
  const tables = tablesResp?.data ?? []
  const bookings = bookingsResp?.data ?? []
  const combinations = combinationsResp?.data ?? []
  const layouts = layoutsResp?.data ?? []

  // Local state for the date-specific time range being edited
  const [editingTimeStart, setEditingTimeStart] = useState(() => {
    const now = new Date()
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  })
  const [editingTimeEnd, setEditingTimeEnd] = useState('23:59')

  const handleNowClick = useCallback(() => {
    const now = new Date()
    setEditingTimeStart(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`)
  }, [])

  // Save confirmation modal (shown when saving a date-specific layout)
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false)

  // Timeout ref for clearing rejected table IDs after the animation completes
  const rejectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // When non-null, the next live→layout mode switch should jump straight into date-edit mode
  const enterDateEditRef = useRef<{
    date: string
    startTime: string
    endTime: string
    layoutId: string | null
  } | null>(null)

  // Reset to layout mode default editing when entering layout mode
  // (unless enterDateEditRef is set — then jump straight into date-edit mode)
  const prevModeRef = useRef(mode)
  useEffect(() => {
    if (mode === 'layout' && prevModeRef.current !== 'layout') {
      if (enterDateEditRef.current) {
        const { date, startTime, endTime, layoutId } = enterDateEditRef.current
        setTimelineDate(date)
        setEditingTimeStart(startTime)
        setEditingTimeEnd(endTime)
        setIsEditingDateLayout(true)
        setActiveLayoutId(layoutId)
        enterDateEditRef.current = null
      } else {
        setIsEditingDateLayout(false)
        setActiveLayoutId(null)
      }
    }
    prevModeRef.current = mode
  }, [mode, setIsEditingDateLayout, setActiveLayoutId, setTimelineDate])

  // Resolve the effective layout(s) for the current context.
  // overlappingLayouts: all layouts whose window covers the current time (may be >1 for :30 bookings).
  // resolvedLayout: primary (first) layout used for editing/positions key.
  const [resolvedLayout, overlappingLayouts] = useMemo((): [typeof layouts[0] | null, typeof layouts] => {
    if (mode === 'layout') {
      if (!isEditingDateLayout) return [null, []]
      const layout = layouts.find((l) => l.documentId === activeLayoutId) ?? null
      return [layout, layout ? [layout] : []]
    }
    // Live mode: collect ALL layouts whose window covers the current date+time.
    // String comparison works correctly for HH:MM ("19:30" >= "19:00" && "19:30" < "20:30").
    const timeMatches = layouts.filter((l) => {
      if (!l.date || l.date !== timelineDate || !l.startTime || !l.endTime) return false
      if (timelineHour === null) return true
      return timelineHour >= l.startTime && timelineHour < l.endTime
    })
    if (timeMatches.length > 0) return [timeMatches[0], timeMatches]
    const fallback = layouts.find((l) => l.date === timelineDate && !l.startTime) ?? null
    return [fallback, fallback ? [fallback] : []]
  }, [mode, isEditingDateLayout, layouts, activeLayoutId, timelineDate, timelineHour])

  // Switch to default layout editing (permanent table positions + combinations)
  const handleEditDefaultLayout = useCallback(() => {
    if (mode === 'layout' && !isEditingDateLayout) return // already there
    if (mode === 'layout') {
      // In date-edit mode — drop back to default without leaving layout mode
      setIsEditingDateLayout(false)
      setActiveLayoutId(null)
      clearDirty()
      prevKeyRef.current = ''
    } else {
      // From live mode — enterDateEditRef stays null so useEffect resets to default
      enterDateEditRef.current = null
      setMode('layout')
    }
  }, [mode, isEditingDateLayout, setIsEditingDateLayout, setActiveLayoutId, clearDirty, setMode])

  // Jump from Live mode into Layout mode pre-targeting the resolved layout
  const handleEditLiveLayout = useCallback(() => {
    const layout = resolvedLayout
    const date = timelineDate
    const startTime = layout?.startTime ?? (timelineHour ?? editingTimeStart)
    const endTime = layout?.endTime ?? '23:59'
    const layoutId = layout?.documentId ?? null

    enterDateEditRef.current = { date, startTime, endTime, layoutId }
    clearDirty()
    setMode('layout')
  }, [resolvedLayout, timelineDate, timelineHour, editingTimeStart, clearDirty, setMode])

  // Combos disabled only for the active date layout (documentIds of global combos to suppress)
  const [localDisabledCombos, setLocalDisabledCombos] = useState(() => new Set<string>())

  // Combos that exist only for the active date layout (not persisted as permanent TableCombination records)
  const [localAddedCombos, setLocalAddedCombos] = useState<Array<{
    tempId: string
    tableIds: number[]
    name: string
    seatsMin: number
    seatsMax: number
  }>>([])

  // Reset local combo state whenever the active layout changes
  useEffect(() => {
    setLocalDisabledCombos(new Set(resolvedLayout?.disabledCombinations ?? []))
    const loaded = (resolvedLayout?.localCombinations ?? []).map((lc, i) => ({
      tempId: `local-loaded-${i}`,
      tableIds: tables.filter((t) => lc.tableDocumentIds.includes(t.documentId)).map((t) => t.id),
      name: lc.name,
      seatsMin: lc.seatsMin,
      seatsMax: lc.seatsMax,
    }))
    setLocalAddedCombos(loaded)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedLayout?.documentId])

  // Effective combinations: permanent list (minus disabled) + local date-specific combos.
  // In live mode, merges combos from ALL overlapping layouts so :30 bookings are always visible.
  const effectiveCombinations = useMemo(() => {
    const disabledIds = new Set([
      ...overlappingLayouts.flatMap((l) => l.disabledCombinations ?? []),
      ...localDisabledCombos,
    ])
    const permanent = combinations.filter((c) => !disabledIds.has(c.documentId))
    const localObjs = localAddedCombos.map((lc) => ({
      id: -1,
      documentId: lc.tempId,
      bookosId: lc.tempId,
      name: lc.name,
      seatsMin: lc.seatsMin,
      seatsMax: lc.seatsMax,
      bookable: true,
      bookableOnline: true,
      bookingPriority: 5,
      internalNote: '',
      tables: tables.filter((t) => lc.tableIds.includes(t.id)),
      area: null as any,
      createdAt: '',
      updatedAt: '',
    }))
    // In live mode, also include localCombinations from non-primary overlapping layouts
    const extraObjs = mode === 'live'
      ? overlappingLayouts.slice(1).flatMap((l) =>
          (l.localCombinations ?? []).map((lc, i) => ({
            id: -1 as const,
            documentId: `extra-${l.documentId}-${i}`,
            bookosId: `extra-${l.documentId}-${i}`,
            name: lc.name,
            seatsMin: lc.seatsMin,
            seatsMax: lc.seatsMax,
            bookable: true,
            bookableOnline: true,
            bookingPriority: 5 as const,
            internalNote: '',
            tables: tables.filter((t) => lc.tableDocumentIds.includes(t.documentId)),
            area: null as any,
            createdAt: '',
            updatedAt: '',
          })),
        )
      : []
    return [...permanent, ...localObjs, ...extraObjs] as typeof combinations
  }, [combinations, overlappingLayouts, localDisabledCombos, localAddedCombos, tables, mode])

  // When time range changes in date-editing mode, find the matching layout
  useEffect(() => {
    if (!isEditingDateLayout || mode !== 'layout') return
    const existing = layouts.find((l) => {
      if (l.date !== timelineDate) return false
      return l.startTime === editingTimeStart && l.endTime === editingTimeEnd
    }) ?? null
    const newId = existing?.documentId ?? null
    if (newId !== activeLayoutId) {
      setActiveLayoutId(newId)
      prevKeyRef.current = ''
    }
  }, [editingTimeStart, editingTimeEnd, isEditingDateLayout, mode, timelineDate, layouts])

  // Initialize positions when tables change OR the effective layout changes
  const prevKeyRef = useRef<string>('')
  useEffect(() => {
    if (tables.length === 0) return
    const editingKey = mode === 'layout' && isEditingDateLayout
      ? resolvedLayout?.documentId ?? `new-date-${timelineDate}`
      : overlappingLayouts.map((l) => l.documentId).join(',') || 'default'
    const key = `${tables.map((t) => t.id).sort().join(',')}_${editingKey}`
    if (key === prevKeyRef.current) return
    prevKeyRef.current = key

    const snapTo = (v: number) => Math.round(v / gridSize) * gridSize

    if (overlappingLayouts.some((l) => l.positions)) {
      // Merge positions from all overlapping layouts: start from table.metadata,
      // then overlay each layout's saved positions (later layouts override earlier ones).
      const posMap: Record<string, { x: number; y: number }> = {}
      for (const t of tables) {
        const meta = t.metadata as TableMetadata | null
        posMap[String(t.id)] =
          meta?.posX != null ? { x: snapTo(meta.posX), y: snapTo(meta.posY) } : { x: 48, y: 48 }
      }
      for (const layout of overlappingLayouts) {
        if (!layout.positions) continue
        for (const t of tables) {
          const saved = layout.positions[t.documentId]
          if (saved) posMap[String(t.id)] = { x: snapTo(saved.posX), y: snapTo(saved.posY) }
        }
      }
      // Preserve unsaved drag positions so adding a table doesn't snap others back
      const { positions: curPos, dirtyIds: curDirty } = useFloorPlanStore.getState()
      for (const dirtyId of curDirty) {
        if (curPos[dirtyId]) posMap[dirtyId] = curPos[dirtyId]
      }
      initPositions(posMap)
    } else {
      // Default: load from table.metadata, auto-save unpositioned tables
      const posMap: Record<string, { x: number; y: number }> = {}
      const tablesToSave: Array<{ documentId: string; metadata: { posX: number; posY: number; rotation: number } }> = []
      const maxW = AREA_WIDTH - 100
      const maxH = AREA_HEIGHT - AREA_HEADER - 80

      for (const t of tables) {
        const meta = t.metadata as TableMetadata | null
        if (meta?.posX != null && meta?.posY != null) {
          posMap[String(t.id)] = { x: snapTo(meta.posX), y: snapTo(meta.posY) }
        } else {
          const idx = tables.filter((tt) => tt.area?.id === t.area?.id).indexOf(t)
          const col = idx % 4
          const row = Math.floor(idx / 4)
          const defaultPos = {
            x: snapTo(Math.min(40 + col * 120, maxW)),
            y: snapTo(Math.min(40 + row * 100, maxH)),
          }
          posMap[String(t.id)] = defaultPos
          // Only auto-save positions for the default layout (not when editing a date)
          if (!isEditingDateLayout) {
            tablesToSave.push({
              documentId: t.documentId,
              metadata: { posX: defaultPos.x, posY: defaultPos.y, rotation: 0 },
            })
          }
        }
      }
      // Preserve unsaved drag positions so adding a table doesn't snap others back
      const { positions: curPos, dirtyIds: curDirty } = useFloorPlanStore.getState()
      for (const dirtyId of curDirty) {
        if (curPos[dirtyId]) posMap[dirtyId] = curPos[dirtyId]
      }
      initPositions(posMap)
      if (tablesToSave.length > 0) {
        batchSave.mutate(tablesToSave)
      }
    }
  }, [tables, overlappingLayouts, isEditingDateLayout, timelineDate, mode, gridSize, initPositions])

  const selectedTable = useMemo(
    () => tables.find((t) => String(t.id) === selectedTableId) ?? null,
    [tables, selectedTableId],
  )

  const selectedArea = useMemo(
    () => areas.find((a) => String(a.id) === selectedAreaId) ?? null,
    [areas, selectedAreaId],
  )

  const currentBooking = useMemo(() => {
    if (!selectedTable) return null
    const tableBookings = bookings.filter(
      (b) =>
        !['canceled', 'no_show'].includes(b.status) &&
        b.bookingTables?.some((bt) => bt.table?.id === selectedTable.id),
    )
    if (tableBookings.length === 0) return null
    if (timelineHour === null) return tableBookings[0]
    const viewTime = new Date(`${timelineDate}T${timelineHour}:00`)
    return tableBookings.find((b) => {
      // Prefer b.date + b.time (local, no tz) over b.dateTime (UTC ISO) to avoid offset shift
      const isoStart = b.date && b.time ? `${b.date}T${b.time}:00` : b.dateTime ?? null
      if (!isoStart) return false
      const start = parseISO(isoStart)
      const end = addMinutes(start, b.duration || 90)
      return !isBefore(viewTime, start) && isBefore(viewTime, end)
    }) ?? null
  }, [selectedTable, bookings, timelineDate, timelineHour])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event
      if (!delta) return
      const id = String(active.id)

      // Combination container drag — move all constituent tables by the same delta
      if (id.startsWith('combo-')) {
        const data = active.data.current as { areaId?: string; tableIds?: number[] }
        const comboAreaId = data?.areaId
        const tableIds = data?.tableIds ?? []
        for (const tableId of tableIds) {
          const cur = positions[String(tableId)] ?? { x: 0, y: 0 }
          setPosition(String(tableId), {
            x: cur.x + delta.x / zoom,
            y: cur.y + delta.y / zoom,
          }, comboAreaId)
        }
        return
      }

      const areaId = (active.data.current as any)?.areaId as string | undefined
      const current = positions[id] ?? { x: 0, y: 0 }

      const rawPos = {
        x: current.x + delta.x / zoom,
        y: current.y + delta.y / zoom,
      }

      // Snapped position for overlap detection (always snap regardless of snapToGrid toggle)
      const snappedPos = {
        x: Math.round(rawPos.x / gridSize) * gridSize,
        y: Math.round(rawPos.y / gridSize) * gridSize,
      }

      // Overlap detection + sibling list (needed for both overlap check and combination creation)
      const draggedTable = tables.find((t) => String(t.id) === id)
      let siblings: typeof tables = []
      if (draggedTable && areaId) {
        const { width: dw, height: dh } = getShape(draggedTable.seatsMax, draggedTable.shape)
        siblings = tables.filter((t) => String(t.area?.id) === areaId && String(t.id) !== id)
        const overlappingSiblings = siblings.filter((t) => {
          const tPos = positions[String(t.id)]
          if (!tPos) return false
          const { width: tw, height: th } = getShape(t.seatsMax, t.shape)
          return (
            snappedPos.x <= tPos.x + tw &&
            snappedPos.x + dw >= tPos.x &&
            snappedPos.y <= tPos.y + th &&
            snappedPos.y + dh >= tPos.y
          )
        })
        if (overlappingSiblings.length > 0) {
          if (mode === 'layout') {
            const allCombinable =
              draggedTable.combinable !== false &&
              overlappingSiblings.every((s) => s.combinable !== false)

            if (allCombinable) {
              // Check if all overlapping siblings belong to the same existing combo
              const sharedCombo = combinations.find((c) =>
                overlappingSiblings.every((s) => c.tables?.some((t) => t.id === s.id)),
              )

              if (sharedCombo) {
                if (!isEditingDateLayout) {
                  // Permanent layout: add dragged table to the existing combo
                  if (!sharedCombo.tables?.some((t) => t.id === draggedTable.id)) {
                    const existingIds = sharedCombo.tables?.map((t) => t.id) ?? []
                    updateCombination.mutate({
                      documentId: sharedCombo.documentId,
                      name: [...(sharedCombo.tables?.map((t) => t.name) ?? []), draggedTable.name].join('+'),
                      seatsMin: (sharedCombo.seatsMin ?? 0) + draggedTable.seatsMin,
                      seatsMax: (sharedCombo.seatsMax ?? 0) + draggedTable.seatsMax,
                      tables: [...existingIds, draggedTable.id],
                    })
                  }
                } else if (localDisabledCombos.has(sharedCombo.documentId)) {
                  // Date override: re-enable a locally disabled permanent combo
                  setLocalDisabledCombos((prev) => {
                    const s = new Set(prev)
                    s.delete(sharedCombo.documentId)
                    return s
                  })
                }
                // Snap dragged table to the closest side of the anchor
                const anchor = overlappingSiblings[0]
                const anchorPos = positions[String(anchor.id)]!
                const { width: aw, height: ah } = getShape(anchor.seatsMax, anchor.shape)
                setPosition(id, snapAdjacentTo(snappedPos, dw, dh, anchorPos, aw, ah), areaId)
                return
              } else if (overlappingSiblings.length === 1) {
                // Single standalone sibling
                const target = overlappingSiblings[0]
                const existingPermCombo = combinations.find(
                  (c) =>
                    c.tables?.some((t) => t.id === draggedTable.id) &&
                    c.tables?.some((t) => t.id === target.id),
                )
                if (!isEditingDateLayout) {
                  // Permanent layout: create new combo
                  if (!existingPermCombo) {
                    createCombination.mutate({
                      bookosId: `combo-${Date.now()}`,
                      name: `${draggedTable.name}+${target.name}`,
                      seatsMin: draggedTable.seatsMin + target.seatsMin,
                      seatsMax: draggedTable.seatsMax + target.seatsMax,
                      bookable: true,
                      bookableOnline: true,
                      bookingPriority: Math.max(
                        draggedTable.bookingPriority ?? 5,
                        target.bookingPriority ?? 5,
                      ),
                      tables: [draggedTable.id, target.id],
                      area: Number(areaId),
                    })
                  }
                } else {
                  // Date override: re-enable disabled permanent combo, or create a local-only combo
                  if (existingPermCombo && localDisabledCombos.has(existingPermCombo.documentId)) {
                    setLocalDisabledCombos((prev) => {
                      const s = new Set(prev)
                      s.delete(existingPermCombo.documentId)
                      return s
                    })
                  } else if (!existingPermCombo) {
                    const alreadyLocal = localAddedCombos.some(
                      (lc) => lc.tableIds.includes(draggedTable.id) && lc.tableIds.includes(target.id),
                    )
                    if (!alreadyLocal) {
                      setLocalAddedCombos((prev) => [
                        ...prev,
                        {
                          tempId: `local-combo-${Date.now()}`,
                          tableIds: [draggedTable.id, target.id],
                          name: `${draggedTable.name}+${target.name}`,
                          seatsMin: draggedTable.seatsMin + target.seatsMin,
                          seatsMax: draggedTable.seatsMax + target.seatsMax,
                        },
                      ])
                    }
                  }
                }
                const tPos = positions[String(target.id)]!
                const { width: tw, height: th } = getShape(target.seatsMax, target.shape)
                setPosition(id, snapAdjacentTo(snappedPos, dw, dh, tPos, tw, th), areaId)
                return
              }
            }
          }
          // Cannot resolve overlap → reject animation + cancel move
          const rejectedIds = new Set<string>([id, ...overlappingSiblings.map((t) => String(t.id))])
          if (rejectionTimeoutRef.current) clearTimeout(rejectionTimeoutRef.current)
          setRejectedTableIds(rejectedIds)
          rejectionTimeoutRef.current = setTimeout(() => {
            setRejectedTableIds(new Set())
            rejectionTimeoutRef.current = null
          }, 1000)
          return
        }
      }

      // Combined-table protection: when editing a date-specific layout, prevent moving
      // a table that is part of an *active* combination booked on this date
      // (uses effectiveCombinations so locally-disabled combos are already excluded)
      if (isEditingDateLayout && draggedTable) {
        const relatedCombos = effectiveCombinations.filter((c) =>
          c.tables?.some((t) => t.id === draggedTable.id),
        )
        for (const combo of relatedCombos) {
          if (!combo.tables || combo.tables.length < 2) continue
          const activeBookings = bookings.filter(
            (b) => !['canceled', 'no_show'].includes(b.status),
          )
          for (const booking of activeBookings) {
            const bookedTableIds = new Set(
              booking.bookingTables?.map((bt) => bt.table?.id).filter(Boolean),
            )
            const allComboTablesBooked = combo.tables.every((t) => bookedTableIds.has(t.id))
            if (allComboTablesBooked) {
              const guestName = booking.guest?.name ?? 'Unknown'
              const time = booking.time ?? ''
              toast.error(
                `Can't separate ${combo.tables.map((t) => t.name).join(' & ')} — booked by ${guestName} at ${time}`,
              )
              return
            }
          }
        }
      }

      setPosition(id, rawPos, areaId)

      // Proximity interaction — in any layout editing mode
      if (mode === 'layout' && draggedTable && areaId) {
        const finalPos = snappedPos
        const { width: dw, height: dh } = getShape(draggedTable.seatsMax, draggedTable.shape)
        const rejectedIds = new Set<string>()
        // Track combos already processed to avoid duplicate mutations for 3+ table combos
        const processedComboIds = new Set<string>()

        for (const sibling of siblings) {
          const sibPos = positions[String(sibling.id)]
          if (!sibPos) continue

          // Search permanent combos (regardless of disabled state)
          const existingPermCombo = combinations.find(
            (c) =>
              c.tables?.some((t) => t.id === draggedTable.id) &&
              c.tables?.some((t) => t.id === sibling.id),
          )
          // Search local date-specific combos
          const existingLocalCombo = localAddedCombos.find(
            (lc) => lc.tableIds.includes(draggedTable.id) && lc.tableIds.includes(sibling.id),
          )

          const isPermActiveCombo =
            existingPermCombo &&
            !localDisabledCombos.has(existingPermCombo.documentId) &&
            !(resolvedLayout?.disabledCombinations?.includes(existingPermCombo.documentId))

          if (isPermActiveCombo && !processedComboIds.has(existingPermCombo.documentId)) {
            // Table is in an active permanent combo — break only when entirely outside the bounding box
            const comboTables = existingPermCombo.tables ?? []
            let cMinX = Infinity, cMinY = Infinity, cMaxX = -Infinity, cMaxY = -Infinity
            for (const ct of comboTables) {
              if (ct.id === draggedTable.id) continue
              const cp = positions[String(ct.id)]
              if (!cp) continue
              const { width: ctw, height: cth } = getShape(ct.seatsMax, ct.shape)
              if (cp.x < cMinX) cMinX = cp.x
              if (cp.y < cMinY) cMinY = cp.y
              if (cp.x + ctw > cMaxX) cMaxX = cp.x + ctw
              if (cp.y + cth > cMaxY) cMaxY = cp.y + cth
            }
            if (isFinite(cMinX)) {
              const stillInside =
                finalPos.x < cMaxX &&
                finalPos.x + dw > cMinX &&
                finalPos.y < cMaxY &&
                finalPos.y + dh > cMinY
              if (!stillInside) {
                processedComboIds.add(existingPermCombo.documentId)
                if (isEditingDateLayout) {
                  // Date override: locally disable the permanent combo
                  setLocalDisabledCombos((prev) => {
                    const s = new Set(prev)
                    s.add(existingPermCombo.documentId)
                    return s
                  })
                } else {
                  const remainingTables = (existingPermCombo.tables ?? []).filter(
                    (t) => t.id !== draggedTable.id,
                  )
                  if (remainingTables.length >= 2) {
                    updateCombination.mutate({
                      documentId: existingPermCombo.documentId,
                      name: remainingTables.map((t) => t.name).join('+'),
                      seatsMin: remainingTables.reduce((s, t) => s + (t.seatsMin ?? 0), 0),
                      seatsMax: remainingTables.reduce((s, t) => s + (t.seatsMax ?? 0), 0),
                      tables: remainingTables.map((t) => t.id),
                    })
                  } else {
                    deleteCombination.mutate(existingPermCombo.documentId)
                  }
                }
              }
            }
          } else if (existingLocalCombo) {
            // Table is in a local date-specific combo — break only when entirely outside the bounding box
            const localComboSiblings = tables.filter(
              (t) => existingLocalCombo.tableIds.includes(t.id) && t.id !== draggedTable.id,
            )
            let cMinX = Infinity, cMinY = Infinity, cMaxX = -Infinity, cMaxY = -Infinity
            for (const ct of localComboSiblings) {
              const cp = positions[String(ct.id)]
              if (!cp) continue
              const { width: ctw, height: cth } = getShape(ct.seatsMax, ct.shape)
              if (cp.x < cMinX) cMinX = cp.x
              if (cp.y < cMinY) cMinY = cp.y
              if (cp.x + ctw > cMaxX) cMaxX = cp.x + ctw
              if (cp.y + cth > cMaxY) cMaxY = cp.y + cth
            }
            if (isFinite(cMinX)) {
              const stillInside =
                finalPos.x < cMaxX &&
                finalPos.x + dw > cMinX &&
                finalPos.y < cMaxY &&
                finalPos.y + dh > cMinY
              if (!stillInside) {
                setLocalAddedCombos((prev) =>
                  prev
                    .map((lc) => {
                      if (lc.tempId !== existingLocalCombo.tempId) return lc
                      const remaining = lc.tableIds.filter((tid) => tid !== draggedTable.id)
                      if (remaining.length < 2) return null
                      const remainingTables = tables.filter((t) => remaining.includes(t.id))
                      return {
                        ...lc,
                        tableIds: remaining,
                        name: remainingTables.map((t) => t.name).join('+'),
                        seatsMin: remainingTables.reduce((s, t) => s + (t.seatsMin ?? 0), 0),
                        seatsMax: remainingTables.reduce((s, t) => s + (t.seatsMax ?? 0), 0),
                      }
                    })
                    .filter(Boolean) as typeof prev,
                )
              }
            }
          } else if (existingPermCombo && isEditingDateLayout &&
            localDisabledCombos.has(existingPermCombo.documentId)) {
            // Disabled permanent combo — re-enable if user drags tables back adjacent
            const { width: sw, height: sh } = getShape(sibling.seatsMax, sibling.shape)
            if (
              finalPos.x <= sibPos.x + sw &&
              finalPos.x + dw >= sibPos.x &&
              finalPos.y <= sibPos.y + sh &&
              finalPos.y + dh >= sibPos.y
            ) {
              setLocalDisabledCombos((prev) => {
                const s = new Set(prev)
                s.delete(existingPermCombo.documentId)
                return s
              })
            }
          } else if (!existingPermCombo && !existingLocalCombo) {
            // No combo at all — combine when tables are adjacent or overlapping
            const { width: sw, height: sh } = getShape(sibling.seatsMax, sibling.shape)
            if (
              finalPos.x <= sibPos.x + sw &&
              finalPos.x + dw >= sibPos.x &&
              finalPos.y <= sibPos.y + sh &&
              finalPos.y + dh >= sibPos.y
            ) {
              const bothCombinable = sibling.combinable !== false && draggedTable.combinable !== false
              if (bothCombinable) {
                if (!isEditingDateLayout) {
                  // Permanent layout: create permanent combo
                  createCombination.mutate({
                    bookosId: `combo-${Date.now()}`,
                    name: `${draggedTable.name}+${sibling.name}`,
                    seatsMin: (draggedTable.seatsMin ?? 0) + (sibling.seatsMin ?? 0),
                    seatsMax: (draggedTable.seatsMax ?? 0) + (sibling.seatsMax ?? 0),
                    bookable: true,
                    bookableOnline: true,
                    bookingPriority: Math.max(
                      draggedTable.bookingPriority ?? 5,
                      sibling.bookingPriority ?? 5,
                    ),
                    tables: [draggedTable.id, sibling.id],
                    area: Number(areaId),
                  })
                } else {
                  // Date override: create local-only combo
                  const alreadyLocal = localAddedCombos.some(
                    (lc) => lc.tableIds.includes(draggedTable.id) && lc.tableIds.includes(sibling.id),
                  )
                  if (!alreadyLocal) {
                    setLocalAddedCombos((prev) => [
                      ...prev,
                      {
                        tempId: `local-combo-${Date.now()}`,
                        tableIds: [draggedTable.id, sibling.id],
                        name: `${draggedTable.name}+${sibling.name}`,
                        seatsMin: (draggedTable.seatsMin ?? 0) + (sibling.seatsMin ?? 0),
                        seatsMax: (draggedTable.seatsMax ?? 0) + (sibling.seatsMax ?? 0),
                      },
                    ])
                  }
                }
              } else {
                rejectedIds.add(String(draggedTable.id))
                rejectedIds.add(String(sibling.id))
              }
            }
          }
        }

        if (rejectedIds.size > 0) {
          if (rejectionTimeoutRef.current) clearTimeout(rejectionTimeoutRef.current)
          setRejectedTableIds(rejectedIds)
          rejectionTimeoutRef.current = setTimeout(() => {
            setRejectedTableIds(new Set())
            rejectionTimeoutRef.current = null
          }, 1000)
        }
      }
    },
    [positions, setPosition, zoom, gridSize, tables, mode, isEditingDateLayout, combinations, effectiveCombinations, bookings, createCombination, updateCombination, deleteCombination, setRejectedTableIds, localDisabledCombos, setLocalDisabledCombos, localAddedCombos, setLocalAddedCombos],
  )

  // Handle clicking a date pill in Layout mode
  const handleLayoutDateSelect = useCallback(
    (date: string | 'default') => {
      if (date === 'default') {
        setIsEditingDateLayout(false)
        setActiveLayoutId(null)
        prevKeyRef.current = ''
      } else {
        setTimelineDate(date)
        setIsEditingDateLayout(true)
        // Find existing layout for this date + current time range
        const existing = layouts.find((l) => {
          if (l.date !== date) return false
          return l.startTime === editingTimeStart && l.endTime === editingTimeEnd
        }) ?? null
        setActiveLayoutId(existing?.documentId ?? null)
        prevKeyRef.current = ''
      }
      clearDirty()
    },
    [layouts, editingTimeStart, editingTimeEnd, setTimelineDate, setIsEditingDateLayout, setActiveLayoutId, clearDirty],
  )

  // Break (dissolve) a combination — permanent delete in default mode, local disable in date mode
  const handleBreakCombo = useCallback((documentId: string) => {
    if (!isEditingDateLayout) {
      // Default layout: permanently delete the combination record via API
      deleteCombination.mutate(documentId)
    } else {
      // Date-specific layout: is it a local-only combo or a permanent one?
      const isLocal = localAddedCombos.some((lc) => lc.tempId === documentId)
      if (isLocal) {
        setLocalAddedCombos((prev) => prev.filter((lc) => lc.tempId !== documentId))
      } else {
        setLocalDisabledCombos((prev) => new Set([...prev, documentId]))
      }
      // Mark dirty so the Save button activates for the date-specific layout
      markDirty('__combo__')
    }
  }, [isEditingDateLayout, deleteCombination, localAddedCombos, setLocalAddedCombos, setLocalDisabledCombos, markDirty])

  // Cancel date-specific layout editing — discard unsaved changes and return to Live mode
  const handleCancelDateEdit = useCallback(() => {
    clearDirty()                 // clear dirty BEFORE re-init so dragged positions aren't preserved
    prevKeyRef.current = ''      // force positions useEffect to re-run from fetched data
    setMode('live')
  }, [clearDirty, setMode])

  // Build a full positions snapshot from current canvas state (clamps to area bounds before persisting)
  const buildPositionsSnapshot = useCallback(() => {
    const { areaBounds } = useFloorPlanStore.getState()
    return Object.fromEntries(
      tables.map((t) => {
        const bounds = areaBounds[String(t.area?.id)]
        const raw = positions[String(t.id)] ?? { x: 0, y: 0 }
        const clamped = bounds
          ? { x: Math.max(0, Math.min(raw.x, bounds.width - 96)), y: Math.max(0, Math.min(raw.y, bounds.height - 64)) }
          : raw
        return [t.documentId, { posX: Math.round(clamped.x), posY: Math.round(clamped.y), rotation: 0 }]
      }),
    )
  }, [tables, positions])

  // Primary save handler
  const handleSave = useCallback(() => {
    if (batchSave.isPending) return
    if (isEditingDateLayout) {
      // Show confirmation modal before committing the date-specific save
      setShowSaveConfirmModal(true)
    } else {
      // Default layout: save dirty positions to table.metadata
      const updates = Array.from(dirtyIds)
        .map((id) => {
          const table = tables.find((t) => String(t.id) === id)
          if (!table) return null
          return {
            documentId: table.documentId,
            metadata: {
              posX: Math.round(positions[id]?.x ?? 0),
              posY: Math.round(positions[id]?.y ?? 0),
              rotation: 0,
            },
          }
        })
        .filter(Boolean) as Array<{ documentId: string; metadata: { posX: number; posY: number; rotation: number } }>
      batchSave.mutate(updates, { onSuccess: () => clearDirty() })
    }
  }, [isEditingDateLayout, dirtyIds, tables, positions, batchSave, clearDirty])

  // Confirmed save for date-specific layout
  const handleConfirmSave = useCallback(() => {
    const allPositions = buildPositionsSnapshot()
    const name = `Override ${timelineDate} ${editingTimeStart}-${editingTimeEnd}`

    const allDisabledCombinations = Array.from(
      new Set([...(resolvedLayout?.disabledCombinations ?? []), ...localDisabledCombos]),
    )

    const allLocalCombinations = localAddedCombos.map((lc) => ({
      tableDocumentIds: tables
        .filter((t) => lc.tableIds.includes(t.id))
        .map((t) => t.documentId),
      name: lc.name,
      seatsMin: lc.seatsMin,
      seatsMax: lc.seatsMax,
    }))

    if (activeLayoutId) {
      updateLayout.mutate(
        { documentId: activeLayoutId, positions: allPositions, disabledCombinations: allDisabledCombinations, localCombinations: allLocalCombinations, autoGenerated: false },
        {
          onSuccess: () => {
            clearDirty()
            setShowSaveConfirmModal(false)
          },
        },
      )
    } else {
      createLayout.mutate(
        {
          name,
          date: timelineDate,
          startTime: editingTimeStart,
          endTime: editingTimeEnd,
          positions: allPositions,
          disabledCombinations: allDisabledCombinations,
          localCombinations: allLocalCombinations,
          autoGenerated: false,
        },
        {
          onSuccess: (resp: any) => {
            setActiveLayoutId(resp?.data?.documentId ?? null)
            clearDirty()
            setShowSaveConfirmModal(false)
          },
        },
      )
    }
  }, [activeLayoutId, resolvedLayout, localDisabledCombos, localAddedCombos, tables, buildPositionsSnapshot, updateLayout, createLayout, clearDirty, timelineDate, editingTimeStart, editingTimeEnd, setActiveLayoutId])

  const handleAreaClick = useCallback(
    (areaId: string) => {
      selectArea(selectedAreaId === areaId ? null : areaId)
    },
    [selectArea, selectedAreaId],
  )

  // Add table modal
  const [showAddTable, setShowAddTable] = useState(false)
  const [addTableAreaId, setAddTableAreaId] = useState<string>('')
  const [newTableName, setNewTableName] = useState('')
  const [newTableSeats, setNewTableSeats] = useState('4')
  const [newTableCombinable, setNewTableCombinable] = useState(true)
  const [newTableShape, setNewTableShape] = useState<TableShape>('square')

  const openAddTable = (areaId?: number) => {
    setAddTableAreaId(areaId ? String(areaId) : '')
    setShowAddTable(true)
  }

  const handleAddTable = () => {
    if (!newTableName || !addTableAreaId) return
    const areaTables = tables.filter((t) => String(t.area?.id) === addTableAreaId)
    const newDims = getShape(Number(newTableSeats), newTableShape)
    const { positions: curPositions, areaBounds: curAreaBounds, gridSize: gs } = useFloorPlanStore.getState()
    const bounds = curAreaBounds[addTableAreaId] ?? { width: AREA_WIDTH, height: AREA_HEIGHT - AREA_HEADER }
    const freePos = findFreePosition(areaTables, curPositions, newDims, bounds.width, bounds.height, gs)
    createTable.mutate(
      {
        name: newTableName,
        seatsMin: 1,
        seatsMax: Number(newTableSeats),
        bookable: true,
        bookableOnline: true,
        bookingPriority: 5,
        combinable: newTableCombinable,
        shape: newTableShape,
        type: 'single',
        deleted: false,
        area: Number(addTableAreaId),
        bookosId: `new-${Date.now()}`,
        metadata: { posX: freePos.x, posY: freePos.y, rotation: 0 },
      } as any,
      {
        onSuccess: (result) => {
          const newTable = (result as any).data ?? result
          if (newTable?.id != null) {
            // Immediately update position in store so table appears without waiting
            // for the reactive tables→useEffect chain
            useFloorPlanStore.getState().setPosition(String(newTable.id), freePos)
          }
          setShowAddTable(false)
          setNewTableName('')
          setNewTableSeats('4')
          setNewTableCombinable(true)
          setNewTableShape('square')
          setAddTableAreaId('')
        },
      },
    )
  }

  // Add area modal
  const [showAddArea, setShowAddArea] = useState(false)
  const [newAreaName, setNewAreaName] = useState('')

  const handleAddArea = () => {
    if (!newAreaName) return
    createArea.mutate(
      {
        bookosId: `a-${Date.now()}`,
        name: newAreaName,
        bookable: true,
        bookableOnline: true,
        bookingPriority: 5,
        restaurant: restaurantId ?? undefined,
      } as any,
      {
        onSuccess: () => {
          setShowAddArea(false)
          setNewAreaName('')
        },
      },
    )
  }

  // Booking dates for timeline navigation — sourced from upcoming 60-day window
  const bookingDates = useMemo(() => {
    const dates = new Set<string>()
    ;(upcomingResp?.data ?? []).filter((b) => !isOrderBooking(b)).forEach((b) => dates.add(b.date))
    return Array.from(dates).sort()
  }, [upcomingResp])

  // Booking times for the selected date — exact HH:MM from each booking.
  const bookingHours = useMemo(() => {
    const times = new Set<string>()
    bookings.forEach((b) => {
      if (!b.time || isOrderBooking(b)) return
      times.add(b.time)
    })
    return Array.from(times).sort()
  }, [bookings])

  const isSaving = activeLayoutId ? updateLayout.isPending : batchSave.isPending || createLayout.isPending

  const saveConfirmLabel = `${editingTimeStart} – ${editingTimeEnd} on ${format(new Date(timelineDate + 'T12:00:00'), 'EEEE, MMMM d', { locale: getDateLocale() })}`

  if (loadingAreas) {
    return (
      <div className="h-[calc(100dvh-8.75rem-env(safe-area-inset-bottom))] md:h-[calc(100dvh-8rem)]">
        <Skeleton variant="rectangular" height="100%" />
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100dvh-8rem-env(safe-area-inset-bottom))] flex-col -mx-3 -mt-3 -mb-[calc(5rem+env(safe-area-inset-bottom))] md:-m-6 md:h-[calc(100dvh-8rem)]">
      {isMobile && (
        <div className="bg-[var(--color-accent-amber-dim)] px-4 py-2 text-xs text-[var(--color-accent-amber)]">
          Switch to tablet or desktop to edit layout
        </div>
      )}

      <FloorPlanToolbar
        onSave={handleSave}
        onAddTable={() => openAddTable()}
        onAddArea={() => setShowAddArea(true)}
        saving={isSaving}
        dirty={dirtyIds.size > 0}
        bookingDates={bookingDates}
        bookingHours={bookingHours}
        editingTimeStart={editingTimeStart}
        editingTimeEnd={editingTimeEnd}
        onNowClick={handleNowClick}
        onTimeStartChange={setEditingTimeStart}
        onTimeEndChange={setEditingTimeEnd}
        onLayoutDateSelect={handleLayoutDateSelect}
        onCancelDateEdit={handleCancelDateEdit}
        resolvedLayoutName={resolvedLayout?.name ?? null}
        onEditLiveLayout={handleEditLiveLayout}
        onEditDefaultLayout={handleEditDefaultLayout}
      />

      <div className="flex-1 flex overflow-hidden relative">
        <FloorPlanCanvas
          areas={areas}
          bookings={bookings}
          combinations={effectiveCombinations}
          onDragEnd={handleDragEnd}
          onAreaClick={handleAreaClick}
          onBreakCombo={handleBreakCombo}
          posOrdersByTableDocumentId={posOrdersByTableDocumentId}
          onPOSTableClick={mode === 'pos' ? handlePOSTableClick : undefined}
        />

        <TableDetailPanel
          table={selectedTable}
          currentBooking={currentBooking}
          combinations={effectiveCombinations}
          onClose={() => selectTable(null)}
          onNewBooking={() => {}}
        />

        <AreaDetailPanel
          area={selectedArea}
          combinations={effectiveCombinations}
          onClose={() => selectArea(null)}
          onAddTable={(areaId) => openAddTable(areaId)}
        />
      </div>

      {/* Add Table Modal — only available when editing default layout */}
      <Modal open={showAddTable} onClose={() => setShowAddTable(false)} title={t('floorPlan.addTable')} size="sm">
        <div className="space-y-4">
          <Input
            label={t('floorPlan.tableName')}
            placeholder="e.g. T1, Window 3"
            value={newTableName}
            onChange={(e) => setNewTableName(e.target.value)}
          />
          <Input
            label={t('floorPlan.maxSeats')}
            type="number"
            value={newTableSeats}
            onChange={(e) => setNewTableSeats(e.target.value)}
          />
          <ShapePicker value={newTableShape} onChange={setNewTableShape} />
          {!addTableAreaId && (
            <Select
              label="Area"
              placeholder="Select area..."
              value={addTableAreaId}
              onChange={(e) => setAddTableAreaId(e.target.value)}
              options={areas.map((a) => ({ value: String(a.id), label: a.name }))}
            />
          )}
          <div className="flex items-center justify-between py-1">
            <div>
              <span className="text-sm text-[var(--color-text-secondary)]">Combinable</span>
              <p className="text-xs text-[var(--color-text-muted)]">Allow combining with other tables</p>
            </div>
            <button
              type="button"
              onClick={() => setNewTableCombinable((v) => !v)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                newTableCombinable ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-bg-elevated)]'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  newTableCombinable ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
          <Button
            className="w-full"
            onClick={handleAddTable}
            loading={createTable.isPending}
            disabled={!newTableName || !addTableAreaId}
          >
            {t('floorPlan.addTable')}
          </Button>
        </div>
      </Modal>

      {/* Add Area Modal */}
      <Modal open={showAddArea} onClose={() => setShowAddArea(false)} title={t('floorPlan.addArea')} size="sm">
        <div className="space-y-4">
          <Input
            label={t('floorPlan.areaName')}
            placeholder="e.g. Terrace, Bar, Private Room"
            value={newAreaName}
            onChange={(e) => setNewAreaName(e.target.value)}
          />
          <Button
            className="w-full"
            onClick={handleAddArea}
            loading={createArea.isPending}
            disabled={!newAreaName}
          >
            {t('floorPlan.addArea')}
          </Button>
        </div>
      </Modal>

      {/* Save confirmation modal for date-specific layout */}
      <Modal
        open={showSaveConfirmModal}
        onClose={() => setShowSaveConfirmModal(false)}
        title="Save date layout?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Save this table arrangement as a layout override for:
          </p>
          <div className="bg-[var(--color-bg-elevated)] rounded-[var(--radius-md)] px-4 py-3 text-sm font-medium text-[var(--color-text-primary)]">
            {saveConfirmLabel}
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            The default layout is not affected. This override only applies to the date and time window shown above.
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowSaveConfirmModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleConfirmSave}
              loading={updateLayout.isPending || createLayout.isPending}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
